<script setup lang="ts">
import { computed, ref } from "vue";
import { api, ServerError } from "@/api/server";
import { useNow } from "@/composables/useNow";
import { useToasts } from "@/composables/useToasts";
import type { ClientCommand, ClientDto, SessionState } from "@/types/protocol";
import { formatAgo, formatDateTime } from "@/utils/format";

const props = defineProps<{ client: ClientDto }>();
const toasts = useToasts();
const now = useNow();
const lastSeen = computed(() => formatAgo(props.client.lastSeen, now.value));

const seconds = ref(30);
const message = ref("");
const busy = ref(false);

const stateText: Record<SessionState, string> = {
  Active: "aktiv",
  Locked: "gesperrt",
  None: "abgemeldet",
};

const commandText: Record<ClientCommand, string> = {
  logoff: "abmelden",
  restart: "neu starten",
  shutdown: "herunterfahren",
};

/** Leeres Feld: der PC nimmt seinen eigenen Standard-Countdown (Agent:DefaultCountdownSeconds). */
const countdownGiven = computed(() => typeof seconds.value === "number" && Number.isFinite(seconds.value));
const countdown = computed(() => (countdownGiven.value && seconds.value > 0 ? seconds.value : 0));

/**
 * „Eingabe": wie lange niemand mehr getippt oder geklickt hat. Ab 5 Minuten zählt der
 * Server die Zeit nicht mehr — deshalb hier dieselbe Schwelle zum Hervorheben.
 */
const idle = computed(() => {
  const value = props.client.idleSeconds;
  if (value === null || value === undefined) return null;
  return {
    stale: value >= 300,
    text: value < 60 ? "gerade eben" : `vor ${Math.floor(value / 60)} min`,
  };
});

/** Fuehrt einen Aufruf aus und meldet Fehler als Toast. */
async function run(action: () => Promise<unknown>, success: string): Promise<void> {
  busy.value = true;
  try {
    await action();
    toasts.info(success);
  } catch (error) {
    toasts.error(error instanceof ServerError ? error.message : String(error));
  } finally {
    busy.value = false;
  }
}

async function sendCommand(command: ClientCommand): Promise<void> {
  const when = !countdownGiven.value
    ? "mit dem Standard-Countdown des PCs"
    : countdown.value > 0
      ? `mit ${countdown.value} s Countdown`
      : "sofort";
  if (!window.confirm(`PC „${props.client.machine}" wirklich ${when} ${commandText[command]}?`)) return;

  await run(
    () => api.command(props.client.machine, command, seconds.value),
    `${commandText[command]} an ${props.client.machine} gesendet (${when}).`,
  );
}

async function sendUpdate(): Promise<void> {
  if (
    !window.confirm(
      `Software-Update auf „${props.client.machine}" anstoßen? Der Dienst auf dem PC startet dabei kurz neu.`,
    )
  )
    return;

  await run(() => api.update(props.client.machine), `Update auf ${props.client.machine} angestoßen.`);
}

async function sendNotice(): Promise<void> {
  const text = message.value.trim();
  if (!text) return;

  await run(() => api.notify(props.client.machine, text), `Nachricht an ${props.client.machine} gesendet.`);
  message.value = "";
}
</script>

<template>
  <article class="panel card">
    <div class="top">
      <span class="machine">{{ client.machine }}</span>
      <span class="badge">{{ client.lastUser ?? "— kein Benutzer —" }}</span>
      <!-- Was dieser PC meldet, nicht was der Server wollte. -->
      <span
        v-if="client.schoolMode"
        class="badge school"
        :class="{ loose: !client.schoolModeEnforced }"
        :title="
          client.schoolModeEnforced
            ? 'Richtlinie greift auf diesem PC'
            : 'Schulmodus wird angezeigt, aber nicht durchgesetzt'
        "
      >
        Schulmodus<template v-if="!client.schoolModeEnforced"> (nur Anzeige)</template>
      </span>
    </div>

    <dl class="kv">
      <dt>Sitzung</dt>
      <dd class="state" :class="client.state.toLowerCase()">{{ stateText[client.state] }}</dd>
      <dt>Vordergrund</dt>
      <dd>
        {{ client.foregroundApp ?? "—" }}
        <span v-if="client.foregroundFullscreen" class="full">Vollbild</span>
      </dd>
      <template v-if="client.audioApp">
        <dt>Ton</dt>
        <dd class="audio">▶ {{ client.audioApp }}</dd>
      </template>
      <template v-if="idle">
        <dt>Eingabe</dt>
        <dd :class="{ idle: idle.stale }">{{ idle.text }}</dd>
      </template>
      <dt>Verbunden</dt>
      <dd>{{ formatDateTime(client.connectedAt) }}</dd>
      <dt>Zuletzt</dt>
      <dd :title="formatDateTime(client.lastSeen)">{{ lastSeen }}</dd>
      <dt>Adresse</dt>
      <dd class="mono">{{ client.remote }}</dd>
      <dt>Version</dt>
      <dd>{{ client.version ? `v${client.version}` : "—" }}</dd>
    </dl>

    <div class="row">
      <label>
        Countdown
        <input v-model.number="seconds" type="number" min="0" max="3600" />
        Sek.
      </label>
    </div>

    <div class="actions">
      <button class="warn" :disabled="busy" @click="sendCommand('logoff')">Abmelden</button>
      <button :disabled="busy" @click="sendCommand('restart')">Neustart</button>
      <button class="danger" :disabled="busy" @click="sendCommand('shutdown')">Herunterfahren</button>
      <button :disabled="busy" @click="sendUpdate">Update</button>
    </div>

    <div class="row">
      <input
        v-model="message"
        type="text"
        placeholder="Nachricht an den PC …"
        @keydown.enter="sendNotice"
      />
      <button :disabled="busy || !message.trim()" @click="sendNotice">Senden</button>
    </div>
  </article>
</template>

<style scoped>
.card {
  padding: 14px 16px;
}

.top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.machine {
  font-weight: 600;
  font-size: 16px;
}

.badge {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--panel2);
}

.badge.school {
  color: var(--accent);
  box-shadow: inset 0 0 0 1px var(--accent);
}

.badge.school.loose {
  color: var(--warn);
  box-shadow: inset 0 0 0 1px var(--warn);
}

.kv {
  color: var(--muted);
  font-size: 13px;
  margin: 8px 0 0;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2px 10px;
}

.kv dt {
  font-weight: 400;
}

.kv dd {
  margin: 0;
  color: var(--fg);
}

.state.active {
  color: var(--ok);
}

.state.locked {
  color: var(--warn);
}

.state.none {
  color: var(--muted);
}

.idle {
  color: var(--warn);
}

/* Laufender Ton ist der Hinweis darauf, dass der PC benutzt wird, obwohl
   niemand tippt - deshalb sichtbar hervorgehoben. */
.audio {
  color: var(--ok);
}

.full {
  color: var(--warn);
  font-size: 11.5px;
  border: 1px solid var(--warn);
  border-radius: 999px;
  padding: 0 6px;
  margin-left: 4px;
}

.actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 10px;
}

.row label {
  color: var(--muted);
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.row label input {
  width: 5.5em;
}

.row input[type="text"] {
  flex: 1;
  min-width: 0;
}
</style>
